import React, { useRef, useState } from 'react';
import axios from 'axios';

const FullInventory = ({
  filteredInventory,
  selectedFormats,
  uniqueFormats,
  toggleFormat,
  searchInput,
  setSearchInput,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalPages,
  refreshInventory
}) => {

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const normalize = (text) => (text || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');

  const sortedInventory = [...filteredInventory].sort((a, b) => {
    const artistA = normalize(a.Artist);
    const artistB = normalize(b.Artist);
    return artistA.localeCompare(artistB);
  });

  const currentItems = sortedInventory.slice(indexOfFirstItem, indexOfLastItem); 

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    setFile(selected);
    setStatus('');
  };

  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setStatus('Clearing existing data...');
      await axios.delete('/api/delete-all');

      setStatus('Deleted all records. Uploading new data...');
      const res = await axios.post('/api/upload-xlsx', formData);

      setStatus(`✅ Uploaded ${res.data.insertedCount} new records.`);
      setFile(null);
      fileInputRef.current.value = null;
      refreshInventory();

    } catch (err) {
      console.error(err);
      setStatus('❌ Upload failed.');
    }
  };

  return (
    <section className="section">
      <h2>Full Inventory</h2>

      {localStorage.getItem('isAdmin') === 'true' && (
        <div className="admin-upload-inline">
          <label className="admin-upload-label">Upload Inventory:</label>

          <input
            type="file"
            accept=".xlsx"
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: 'none' }}
          />

          <button
            className="admin-upload-button small"
            onClick={handleBoxClick}
          >
            {file ? file.name : 'Choose File'}
          </button>

          <button
            className="admin-upload-button small"
            onClick={handleUpload}
            disabled={!file}
          >
            Upload
          </button>

          <span className="admin-upload-status small">{status}</span>
        </div>
      )}

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          flexWrap: 'wrap', gap: '8px', marginBottom: '10px'
        }}>
          <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Search by:</span>
          {uniqueFormats.map((format, i) => (
            <button
              key={i}
              onClick={() => toggleFormat(format)}
              className={`format-button ${selectedFormats.includes(format) ? 'active' : ''}`}
            >
              {format}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();  // Prevent page reload
              setSearchTerm(searchInput);
              setCurrentPage(1);
            }
          }}          
          style={{
            padding: '10px', width: '60%', maxWidth: '400px',
            fontSize: '16px', border: '1px solid #ccc', borderRadius: '6px'
          }}
        />
        <button
          onClick={() => {
            setSearchTerm(searchInput);
            setCurrentPage(1);
          }}
          style={{
            backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: '20px', marginLeft: '-35px', padding: '0', color: '#333'
          }}
          aria-label="Search"
        >
          🔍
        </button>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ backgroundColor: "#ddd" }}>
          <th style={{ padding: "8px", border: "1px solid #ccc" }}>Item Number</th>
          <th style={{ padding: "8px", border: "1px solid #ccc" }}>Format</th>
          <th style={{ padding: "8px", border: "1px solid #ccc" }}>Artist</th>
          <th style={{ padding: "8px", border: "1px solid #ccc" }}>Title</th>
          <th style={{ padding: "8px", border: "1px solid #ccc" }}>S/N</th>
        </tr>
      </thead>
        <tbody>
          {currentItems.length === 0 ? (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                No Results Found.
              </td>
            </tr>
          ) : (
            currentItems.map((item, i) => (
              <tr key={i}>
                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{item['#'] || ''}</td>
                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{item.Format || ''}</td>
                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{item.Artist || ''}</td>
                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{item.Title || ''}</td>
                <td style={{ padding: "8px", border: "1px solid #ccc" }}>{item['S/N'] || ''}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="pagination-button"
        >
          ← Prev
        </button>

        {(() => {
          const maxButtons = 5;
          let startPage = Math.max(currentPage - Math.floor(maxButtons / 2), 1);
          let endPage = startPage + maxButtons - 1;

          if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(endPage - maxButtons + 1, 1);
          }

          return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
            const page = startPage + i;
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`pagination-button ${currentPage === page ? 'active' : ''}`}
              >
                {page}
              </button>
            );
          });
        })()}

        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="pagination-button"
        >
          Next →
        </button>
      </div>
    </section>
  );
};

export default FullInventory;
